if (!customElements.get('request-quote-modal-opener')) {
  // This file's own <script> tag gets included once per request-quote-
  // modal.liquid render, and that snippet legitimately renders more than
  // once on a single product page (the metafield-driven buy-button quote
  // modal AND the bulk-pricing-table's own "-bulk" instance both include
  // it) - without this guard, the bare class declaration below throws
  // "already declared" on the second load and the file's script tag never
  // finishes evaluating.
  class RequestQuoteModalOpener extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.modal = document.querySelector(this.dataset.modal);
      if (!this.button || !this.modal) return;
      this.button.addEventListener('click', () => this.open());
    }

    open() {
      // The modal is rendered wherever the buy-buttons block sits in the
      // page, which on the product template is inside the sticky product
      // info column (position:sticky + z-index:2). That creates its own
      // stacking context, which traps the modal's own z-index:999 inside
      // it - from the sticky header's point of view (z-index:99, outside
      // that trapped context) the whole column only counts as "2", so the
      // header renders on top of the modal instead of under it. Moving the
      // modal to be a direct child of <body> escapes that trapped context
      // entirely; all of its own listeners/state move with it untouched.
      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }
      this.modal.classList.add('request-quote-modal--open');
      document.body.classList.add('overflow-hidden');
      const firstField = this.modal.querySelector('input, textarea');
      if (firstField) firstField.focus();
    }
  }
  customElements.define('request-quote-modal-opener', RequestQuoteModalOpener);
}

// This file's own <script> tag is included once per request-quote-
// modal.liquid render, and (see the customElements guard above) that
// snippet legitimately renders more than once on a single product page. The
// querySelectorAll below already finds *every* modal instance on the page
// in one pass, so running this whole block a second time only attaches a
// second, redundant copy of every listener to the same elements - which is
// what let an empty/invalid form fall through to a real native submit
// (Shopify's own /challenge page) instead of staying blocked by
// validateForm(). Guard it the same way the class declaration is guarded.
if (!window.requestQuoteModalWired) {
  window.requestQuoteModalWired = true;
  wireUpRequestQuoteModals();
}

function wireUpRequestQuoteModals() {
document.querySelectorAll('.request-quote-modal').forEach((modal) => {
  const close = () => {
    modal.classList.remove('request-quote-modal--open');
    document.body.classList.remove('overflow-hidden');
  };

  modal.querySelectorAll('[data-request-quote-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  modal.addEventListener('keyup', (event) => {
    if (event.code === 'Escape') close();
  });

  const form = modal.querySelector('.request-quote-form');
  const successEl = modal.querySelector('.request-quote-modal__success');
  const errorEl = modal.querySelector('.request-quote-modal__error');

  if (!form) return;

  const strings = window.requestQuoteStrings || {};
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const showFieldError = (field, message) => {
    const wrapper = field.closest('[data-field-wrapper]');
    if (!wrapper) return;
    const errorSpan = wrapper.querySelector('[data-field-error]');
    field.classList.add('request-quote-form__field--invalid');
    if (errorSpan) {
      errorSpan.textContent = message;
      errorSpan.hidden = false;
    }
  };

  const clearFieldError = (field) => {
    const wrapper = field.closest('[data-field-wrapper]');
    if (!wrapper) return;
    const errorSpan = wrapper.querySelector('[data-field-error]');
    field.classList.remove('request-quote-form__field--invalid');
    if (errorSpan) {
      errorSpan.textContent = '';
      errorSpan.hidden = true;
    }
  };

  const validateField = (field) => {
    const rule = field.dataset.validate;
    if (!rule) return true;

    if (rule === 'required') {
      if (!field.value.trim()) {
        // This generic rule is shared by the Name and Phone fields - pick a
        // message that actually matches which one is empty instead of
        // always showing the Name field's wording.
        var requiredMessage = strings.fieldRequired || 'This field is required.';
        if (field.type === 'tel') {
          requiredMessage = strings.phoneRequired || 'Please enter your phone number.';
        } else if (field.name === 'contact[name]') {
          requiredMessage = strings.nameRequired || 'Please enter your name.';
        }
        showFieldError(field, requiredMessage);
        return false;
      }
    }

    if (rule === 'required-email') {
      if (!field.value.trim()) {
        showFieldError(field, strings.emailRequired || 'Please enter your email address.');
        return false;
      }
      if (!EMAIL_PATTERN.test(field.value.trim())) {
        showFieldError(field, strings.emailInvalid || 'Please enter a valid email address.');
        return false;
      }
    }

    if (rule === 'required-quantity') {
      const value = parseInt(field.value, 10);
      if (!value || value < 1) {
        showFieldError(field, strings.quantityRequired || 'Please enter a quantity of at least 1.');
        return false;
      }
    }

    clearFieldError(field);
    return true;
  };

  form.querySelectorAll('[data-validate]').forEach((field) => {
    field.addEventListener('blur', () => validateField(field));
    field.addEventListener('input', () => {
      if (field.classList.contains('request-quote-form__field--invalid')) validateField(field);
    });
  });

  const validateForm = () => {
    const fields = Array.from(form.querySelectorAll('[data-validate]'));
    const results = fields.map(validateField);
    const firstInvalid = fields[results.indexOf(false)];
    if (firstInvalid) firstInvalid.focus();
    return results.every(Boolean);
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    errorEl.hidden = true;

    if (!validateForm()) return;

    const submitButton = form.querySelector('button[type="submit"]');
    submitButton.disabled = true;

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('request-failed');
        form.hidden = true;
        successEl.hidden = false;
      })
      .catch(() => {
        errorEl.querySelector('span').textContent = strings.error || 'Something went wrong. Please try again.';
        errorEl.hidden = false;
        submitButton.disabled = false;
      });
  });
});
}

// Auto-open on arrival: collection-card "Request a Quote" links (and the
// bulk pricing table's "contact us for higher quantities" link) send the
// shopper to the product page with ?quote=1, so the modal opens right away
// instead of making them find the button again.
(function () {
  var params = new URLSearchParams(window.location.search);
  if (params.get('quote') !== '1') return;

  var opener = document.querySelector('request-quote-modal-opener');
  if (!opener || typeof opener.open !== 'function') return;

  // Custom elements upgrade synchronously as the parser reaches them, and
  // this script is deferred (runs after the whole document is parsed), so
  // .open() is safe to call immediately - no need to wait for another event.
  opener.open();
})();
